import { FormManager } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';
import difference from 'lodash/difference';
import union from 'lodash/union';

import { Errors } from '../../common/helpers';
import { Permission } from '../../__generated__/graphql';
import { useSession } from '../Auth/SessionProvider';
import UI from '../../common/UI';

const implicitPermissions: {
  [key in Permission]?: Permission[]
} = {
  [Permission.admin]: [
    Permission.edit_events,
    Permission.event_dashboards,
    Permission.edit_registrations,
    Permission.view_registrations,
    Permission.exports,
    Permission.financial_reports,
    Permission.refunds,
    Permission.messages,
  ],
  [Permission.edit_registrations]: [
    Permission.view_registrations,
  ],
  [Permission.exports]: [
    Permission.view_registrations,
  ],
  [Permission.refunds]: [
    Permission.edit_registrations,
    Permission.view_registrations,
  ],
  [Permission.participant_logistics]: [
    Permission.view_registrations,
  ],
  [Permission.messages]: [
    Permission.view_registrations,
  ],
};

export interface EditPermissionsFormProps {
  form: FormManager<{ permissions: Permission[]; }>;
  errors?: Errors;
}

const EditPermissionsForm = ({
  form, errors,
}: EditPermissionsFormProps) => {
  const { t } = useTranslation();
  const { permissions: availablePermissions } = useSession();

  const hasPermission = (permission: Permission, list: Permission[]) => list.indexOf(permission) > -1;

  const togglePermission = (permission: Permission) => {
    form.set('permissions', (values: Permission[]) => {
      const newValues = hasPermission(permission, values)
        ? difference(values, [permission])
        : union(values, [permission, ...implicitPermissions[permission] || []]);

      return newValues.filter((value) => !isDisabled(value, newValues));
    });
  };

  const fullUserPermissions = availablePermissions.filter((permission) => permission !== Permission.admin);
  const userRole = form.values.permissions.includes(Permission.admin) ? 'admin' : 'user';

  /**
   * A permission is disabled if it does not have ALL of its dependent permissions.
   */
  const isDisabled = (permission: Permission, list: Permission[]) => implicitPermissions[permission] && (
    implicitPermissions[permission].reduce((invalid, key: Permission) => invalid || !hasPermission(key, list), false)
  );

  const setAdmin = () => form.set('permissions', availablePermissions);
  const setUser = (permissions: Permission[] = []) => form.set('permissions', permissions);

  const indents: Record<string, number> = {
    [Permission.edit_registrations]: 1,
    [Permission.participant_logistics]: 1,
    [Permission.refunds]: 2,
    [Permission.exports]: 1,
    [Permission.messages]: 1,
  };

  return (
    <>
      <UI.InputGroup sc={{ valid: userRole === 'user' || !errors.permissions, touched: form.touched.permissions }}>
        <UI.InputLabel>
          {t('user_role')}
          {' '}
          <UI.RequiredMark />
        </UI.InputLabel>
        <UI.Radio
          checked={userRole === 'user'}
          onChange={() => setUser()}
          sc={{ box: true, block: true, mb: 1 }}
        >
          <UI.Strong sc={{ block: true }}>
            {t('normal_user')}
          </UI.Strong>
          {t('normal_user_role_description')}
        </UI.Radio>
        <UI.Radio
          checked={userRole === 'admin'}
          onChange={() => setAdmin()}
          sc={{ box: true, block: true }}
        >
          <UI.Strong sc={{ block: true }}>
            {t('administrator')}
          </UI.Strong>
          {t('admin_role_description')}
        </UI.Radio>
        {userRole === 'admin' && <UI.ErrorMessages attribute={t('user_role')} errors={errors.permissions} />}
      </UI.InputGroup>
      {userRole === 'user' && (
        <UI.InputGroup sc={{ valid: !errors.permissions, touched: form.touched.permissions }}>
          <UI.InputLabel>
            {t('permissions_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('user_permissions_description')}
          </UI.InputDescription>
          <UI.Div sc={{ my: 1 }}>
            <UI.A
              onClick={() => setUser(fullUserPermissions)}
              sc={{ disabled: form.values.permissions.length === fullUserPermissions.length }}
            >
              {t('select_all')}
            </UI.A>
            <UI.Span sc={{ muted: true }}> • </UI.Span>
            <UI.A
              onClick={() => setUser([])}
              sc={{ disabled: form.values.permissions.length === 0 }}
            >
              {t('deselect_all')}
            </UI.A>
          </UI.Div>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            {fullUserPermissions.map((permission) => (
              <UI.Checkbox
                checked={hasPermission(permission, form.values.permissions)}
                onChange={() => togglePermission(permission)}
                disabled={isDisabled(permission, form.values.permissions)}
                key={permission}
                sc={{ ml: (indents[permission] ?? 0) * 4 }}
              >
                <UI.Span sc={{ block: true, fontWeight: 500 }}>
                  {t(`permissions.${permission}`)}
                </UI.Span>
                <UI.Span sc={{ color: 'gray.500' }}>
                  {t(`permissions.${permission}_description`)}
                </UI.Span>
              </UI.Checkbox>
            ))}
          </UI.FormGrid>
          <UI.ErrorMessages attribute={t('permissions_title')} errors={errors.permissions} />
        </UI.InputGroup>
      )}
    </>
  );
};

export default EditPermissionsForm;
